import React from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Button from '@/UIlibrary/Button/Button';
import Select from '@/UIlibrary/Select/Select';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';
import { ChevronDown, ChevronUp, HelpCircle } from 'react-feather';
import { useTranslations } from 'use-intl';
import { ToolInstructionList } from './ToolInstructionList';
import { VersionHistoryPanel } from '@/components/VersionHistoryPanel';
import { AgentContextVariable, ToolInstructionWithTools } from '@/pages/CustomerSupport/types';

function SectionHeader({ title, hint }: { title: string; hint?: string }) {
	return (
		<div className="flex items-center gap-2 mb-4">
			<Typography type="text" weight="semibold">
				{title}
			</Typography>
			{hint && (
				<Tooltip content={hint}>
					<HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
				</Tooltip>
			)}
		</div>
	);
}

function ConfigSection({
	title,
	hint,
	children,
	className = ''
}: {
	title: string;
	hint?: string;
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<div className={`rounded-lg border border-gray-200 bg-white p-5 ${className}`}>
			<SectionHeader title={title} hint={hint} />
			{children}
		</div>
	);
}

const MODEL_OPTIONS = [
	'gpt-5.1',
	'gpt-5',
	'gpt-5-mini',
	'gpt-5-nano',
	'gpt-4.1',
	'gpt-4.1-mini',
	'gpt-4.1-nano',
	'gpt-4o',
	'gpt-4o-mini'
];

interface ConfigurationTabProps {
	agentId: string;
	model: string;
	systemPrompt: string;
	isSaving: boolean;
	hasChanges: boolean;
	contextVariables: AgentContextVariable[];
	instructions: ToolInstructionWithTools[];
	isLoadingTools: boolean;
	storeError: string | null;
	isVariablesOpen: boolean;
	isToolInstructionsExpanded: boolean;
	onModelChange: (value: string) => void;
	onSystemPromptChange: (value: string) => void;
	onSave: () => void;
	onToggleVariables: () => void;
	onToggleInstructions: () => void;
	onAddInstruction: () => void;
	onEditInstruction: (instruction: ToolInstructionWithTools) => void;
	onDeleteInstruction: (id: string) => void;
}

export const ConfigurationTab: React.FC<ConfigurationTabProps> = ({
	agentId,
	model,
	systemPrompt,
	isSaving,
	hasChanges,
	contextVariables,
	instructions,
	isLoadingTools,
	storeError,
	isVariablesOpen,
	isToolInstructionsExpanded,
	onModelChange,
	onSystemPromptChange,
	onSave,
	onToggleVariables,
	onToggleInstructions,
	onAddInstruction,
	onEditInstruction,
	onDeleteInstruction
}) => {
	const t = useTranslations('ManageAgents');

	return (
		<div className="w-full space-y-6 mt-4">
			<div className="grid gap-6">
				<ConfigSection title={t('Model')} hint={t('ModelSelectHint')}>
					<div className="max-w-xs">
						<Select
							name="model"
							label=""
							value={model}
							onChange={(e) => onModelChange(e.target.value)}
							options={MODEL_OPTIONS.map((opt) => ({ value: opt, label: opt }))}
							disabled={isSaving}
						/>
					</div>
				</ConfigSection>

				<ConfigSection title={t('SystemPrompt')} hint={t('SystemPromptHint')}>
					<TextArea
						name="systemPrompt"
						id="systemPrompt"
						label=""
						value={systemPrompt}
						onChange={(e) => onSystemPromptChange(e.target.value)}
						disabled={isSaving}
						rows={8}
						placeholder={t('SystemPromptPlaceholder')}
					/>
					{agentId && (
						<div className="mt-4">
							<VersionHistoryPanel entityId={agentId} contentKey="system_prompt" />
						</div>
					)}
				</ConfigSection>

				{hasChanges && (
					<div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-center justify-between">
						<Typography type="text" size="sm" color="subtitle">
							{t('UnsavedChanges')}
						</Typography>
						<Button
							onClick={onSave}
							color="primary"
							size="auto"
							label={isSaving ? t('Saving') : t('SaveChanges')}
							disabled={isSaving}
						/>
					</div>
				)}

				<ConfigSection
					title={t('AvailableContextVariables')}
					hint={t('ContextVariablesDescription')}
					className="overflow-hidden"
				>
					<button
						type="button"
						onClick={onToggleVariables}
						className="w-full flex items-center justify-between py-2 transition-colors hover:bg-gray-50 rounded -m-2 p-2"
					>
						<span className="text-sm text-gray-600">
							{contextVariables.length} {t('VariablesAvailable')}
						</span>
						{isVariablesOpen ? (
							<ChevronUp size={18} className="text-gray-500" />
						) : (
							<ChevronDown size={18} className="text-gray-500" />
						)}
					</button>
					{isVariablesOpen && (
						<div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
							{contextVariables.length === 0 ? (
								<Typography type="text" color="subtitle" size="sm">
									{t('NoContextVariablesAvailable')}
								</Typography>
							) : (
								<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
									{contextVariables.map((variable) => (
										<div
											key={variable.id}
											className="p-3 bg-gray-50 rounded-md border border-gray-100"
										>
											<div className="flex items-center gap-2">
												<Typography type="text" weight="medium" size="sm">
													{variable.name}
												</Typography>
												<span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
													{variable.data_type}
												</span>
											</div>
											<div className="mt-1 line-clamp-2">
												<Typography type="text" color="subtitle" size="sm">
													{variable.description}
												</Typography>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					)}
				</ConfigSection>

				<ConfigSection title={t('ToolInstructions')} hint={t('ToolInstructionsExplanation')}>
					<div className="flex items-center justify-between mb-4">
						<span className="text-sm text-gray-600">
							{instructions.length} {t('InstructionsConfigured')}
						</span>
						<div className="flex gap-2">
							{isToolInstructionsExpanded ? (
								<button
									type="button"
									onClick={onToggleInstructions}
									className="text-sm text-gray-500 hover:text-gray-700"
								>
									{t('CollapseInstructions')}
								</button>
							) : (
								<button
									type="button"
									onClick={onToggleInstructions}
									className="text-sm text-primary-600 hover:text-primary-700"
								>
									{t('ViewInstructions')}
								</button>
							)}
							<Button
								onClick={onAddInstruction}
								color="primary"
								size="auto"
								label={t('AddInstruction')}
							/>
						</div>
					</div>
					{isToolInstructionsExpanded && (
						<div className="max-h-80 overflow-y-auto border border-gray-100 rounded-lg p-2">
							<ToolInstructionList
								instructions={instructions}
								onEdit={onEditInstruction}
								onDelete={onDeleteInstruction}
							/>
						</div>
					)}
				</ConfigSection>
			</div>

			{storeError && <p className="text-red-600 text-sm">{storeError}</p>}
			{isLoadingTools && <p className="text-gray-500 text-sm">Loading tools...</p>}
		</div>
	);
};
